/**
 * Encryption utilities using Web Crypto API
 * Provides secure encryption/decryption of note content
 */

import { logError } from './logger';

// Constants for encryption
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const LEGACY_STORAGE_KEY = 'note_encryption_key';
const LEGACY_ENABLED_KEY = 'encryption_enabled';
const STATE_STORAGE_PREFIX = 'note_encryption_state:';

const requireUserId = (userId) => {
  if (typeof userId !== 'string' || !userId.trim()) {
    throw new Error('A nonempty user ID is required for encryption state.');
  }
  return userId;
};

const keyToBase64 = async (key) => {
  const exportedKey = await window.crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exportedKey)));
};

const base64ToKey = async (keyBase64) => {
  const keyBuffer = Uint8Array.from(atob(keyBase64), c => c.charCodeAt(0));
  const key = await window.crypto.subtle.importKey(
    'raw', keyBuffer, { name: ALGORITHM, length: KEY_LENGTH }, true, ['encrypt', 'decrypt']
  );
  const testData = new TextEncoder().encode('test');
  await window.crypto.subtle.encrypt(
    { name: ALGORITHM, iv: window.crypto.getRandomValues(new Uint8Array(12)) }, key, testData
  );
  return key;
};

function getEncryptionStateStorageKey(userId) {
  return `${STATE_STORAGE_PREFIX}${encodeURIComponent(requireUserId(userId))}`;
}

/**
 * Generates a new AES-256-GCM encryption key using Web Crypto API.
 * 
 * The key is generated with extractable=true to allow export for storage.
 * Uses AES-GCM algorithm with 256-bit key length for strong encryption.
 * 
 * @returns {Promise<CryptoKey>} A CryptoKey object that can be used for encryption/decryption
 * @throws {Error} If key generation fails (e.g., Web Crypto API not available)
 * 
 * @example
 * const key = await generateEncryptionKey();
 * const encrypted = await encryptData('secret data', key);
 */
async function generateEncryptionKey() {
  try {
    const key = await window.crypto.subtle.generateKey(
      {
        name: ALGORITHM,
        length: KEY_LENGTH,
      },
      true, // extractable
      ['encrypt', 'decrypt']
    );
    return key;
  } catch (error) {
    logError('Error generating encryption key', error);
    throw new Error('Failed to generate encryption key');
  }
}

/**
 * Encrypts data using AES-GCM encryption with a random IV.
 * 
 * The function generates a random 12-byte IV (Initialization Vector) for each encryption,
 * ensuring that encrypting the same data multiple times produces different ciphertexts.
 * The IV is prepended to the encrypted data for use during decryption.
 * 
 * Security considerations:
 * - Uses AES-GCM which provides both confidentiality and authenticity
 * - Random IV prevents pattern analysis attacks
 * - IV is included in output for decryption
 * 
 * @param {string} data - Plaintext data to encrypt
 * @param {CryptoKey} key - Encryption key (must support 'encrypt' operation)
 * @returns {Promise<string>} Base64-encoded string containing IV + encrypted data
 * @throws {Error} If encryption fails (invalid key, data too large, etc.)
 * 
 * @example
 * const encrypted = await encryptData('sensitive note content', encryptionKey);
 * // Returns: base64 string with format: [12-byte IV][encrypted data]
 */
async function encryptData(data, key) {
  try {
    // Generate random IV (Initialization Vector)
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Convert data to buffer
    const encodedData = new TextEncoder().encode(data);
    
    // Encrypt the data
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      encodedData
    );
    
    // Combine IV and encrypted data
    const combined = new Uint8Array([...iv, ...new Uint8Array(encryptedData)]);
    
    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    logError('Error encrypting data', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts data that was encrypted with encryptData().
 * 
 * Extracts the IV from the beginning of the encrypted data and uses it
 * along with the provided key to decrypt the ciphertext.
 * 
 * Security considerations:
 * - Validates key exists before attempting decryption
 * - Uses the same IV that was used during encryption
 * - Throws error if decryption fails (wrong key, corrupted data, etc.)
 * 
 * @param {string} encryptedData - Base64-encoded encrypted data (with IV prepended)
 * @param {CryptoKey} key - Decryption key (must support 'decrypt' operation)
 * @returns {Promise<string|null>} Decrypted plaintext, or null if key is missing
 * @throws {Error} If decryption fails (wrong key, corrupted data, invalid format)
 * 
 * @example
 * const decrypted = await decryptData(encryptedString, encryptionKey);
 * // Returns: original plaintext string
 */
async function decryptData(encryptedData, key) {
  try {
    if (!key) {
      logError('No encryption key provided for decryption');
      return null;
    }

    // Convert base64 to array buffer
    const encryptedBuffer = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    // Extract IV from the beginning of the encrypted data
    const iv = encryptedBuffer.slice(0, 12);
    const data = encryptedBuffer.slice(12);

    // Decrypt the data
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    // Convert the decrypted array buffer back to text
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    logError('Error decrypting data', error);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Stores an encryption key in browser localStorage.
 * 
 * The key is exported to raw format, base64-encoded, and stored in localStorage.
 * This allows the key to persist across browser sessions.
 * 
 * Security considerations:
 * - Keys are stored in localStorage (vulnerable to XSS attacks)
 * - Keys are base64-encoded but not encrypted at rest
 * - Consider using cloud key backup for additional security
 * 
 * @param {CryptoKey} key - The encryption key to store
 * @returns {Promise<void>}
 * @throws {Error} If key export or storage fails
 * 
 * @example
 * const key = await generateEncryptionKey();
 * await storeEncryptionState(userId, key, true);
 */
async function storeEncryptionState(userId, key, enabled, canCommit = () => true) {
  try {
    const keyBase64 = await keyToBase64(key);
    if (!canCommit()) return false;
    localStorage.setItem(getEncryptionStateStorageKey(userId), JSON.stringify({
      key: keyBase64,
      enabled: enabled === true,
    }));
    return true;
  } catch (error) {
    logError('Error storing encryption state', error);
    throw new Error('Failed to store encryption state');
  }
}

/**
 * Retrieves and validates the UID-scoped encryption state from localStorage.
 * 
 * The function retrieves the base64-encoded key, converts it back to a CryptoKey,
 * and validates it by attempting a test encryption. This ensures the key is
 * still valid and hasn't been corrupted.
 * 
 * @returns {Promise<{key: CryptoKey, enabled: boolean}|null>} The state, or null if not found or invalid
 * 
 * @example
 * const state = await getStoredEncryptionState(userId);
 * if (state?.key) {
 *   // Key exists and is valid
 *   const decrypted = await decryptData(encryptedData, key);
 * }
 */
async function getStoredEncryptionState(userId) {
  try {
    const serializedState = localStorage.getItem(getEncryptionStateStorageKey(userId));
    if (!serializedState) return null;
    const state = JSON.parse(serializedState);
    if (!state || typeof state.key !== 'string' || typeof state.enabled !== 'boolean') return null;
    const key = await base64ToKey(state.key);
    return { key, enabled: state.enabled };
  } catch (error) {
    logError('Error retrieving encryption state', error);
    return null;
  }
}

async function getStoredEncryptionKey(userId) {
  const state = await getStoredEncryptionState(userId);
  return state?.key || null;
}

async function getLegacyStoredEncryptionKey() {
  try {
    const storedKey = localStorage.getItem(LEGACY_STORAGE_KEY);
    return storedKey ? await base64ToKey(storedKey) : null;
  } catch (error) {
    logError('Error retrieving legacy encryption key', error);
    return null;
  }
}

function getLegacyEncryptionPreference() {
  return localStorage.getItem(LEGACY_ENABLED_KEY) === 'true';
}

/**
 * Checks whether encryption is enabled in the UID-scoped local state.
 * 
 * This is a simple check that doesn't validate the key - use getStoredEncryptionKey()
 * if you need to actually use the key.
 * 
 * @returns {boolean} True if encryption key exists in localStorage, false otherwise
 * 
 * @example
 * if (isEncryptionEnabled()) {
 *   console.log('Encryption is enabled');
 * }
 */
async function isEncryptionEnabled(userId) {
  const state = await getStoredEncryptionState(userId);
  return state?.enabled === true;
}

export {
  generateEncryptionKey,
  encryptData,
  decryptData,
  storeEncryptionState,
  getStoredEncryptionKey,
  getStoredEncryptionState,
  getLegacyStoredEncryptionKey,
  getLegacyEncryptionPreference,
  getEncryptionStateStorageKey,
  isEncryptionEnabled
};
